<template>
  <v-container fluid class="pa-0">
    <!-- Top bar -->
    <v-toolbar dense flat color="grey lighten-3">
      <v-toolbar-title>Campaign Create</v-toolbar-title>
      <v-spacer />
      <v-btn
        :loading="loading"
        :disabled="loading"
        color="primary"
        dark
        @click="submit"
      >
        Save
      </v-btn>
      <v-btn :disabled="loading" color="primary" text @click="close">
        Cancel
      </v-btn>
    </v-toolbar>
    <v-container fluid class="touch-view-container">
      <v-row dense>
        <!-- LEFT: main -->
        <v-col cols="12" md="8">
          <v-card outlined class="card-soft">
            <v-card-text class="pa-0">
              <v-tabs v-model="tab" background-color="grey lighten-4" grow>
                <v-tab>Basics</v-tab>
                <v-tab>Settings</v-tab>
                <v-tab v-if="Campaign.type === 'sponsor'">Details</v-tab>
              </v-tabs>

              <v-divider />

              <v-tabs-items v-model="tab">
                <!-- BASICS -->
                <v-tab-item>
                  <v-card-text class="pa-4">
                    <v-row dense>
                      <v-col cols="12">
                        <div class="d-flex align-center flex-wrap">
                          <div class="font-weight-bold mr-3">Donation Type</div>
                          <v-btn-toggle
                            active-class="custom-active-class"
                            mandatory
                            dense
                            v-model="Campaign.type"
                            color="primary"
                            group
                          >
                            <v-btn value="donation" outlined class="ma-1"
                              >Donation</v-btn
                            >
                            <v-btn value="sponsor" outlined class="ma-1"
                              >Sponsor</v-btn
                            >
                          </v-btn-toggle>

                          <v-spacer />

                          <div class="d-flex align-center">
                            <span class="font-weight-bold mr-2">Publish</span>
                            <v-switch
                              inset
                              v-model="Campaign.isActive"
                              class="ma-0"
                            />
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="12">
                        <v-text-field
                          outlined
                          dense
                          autofocus
                          v-model="Campaign.name"
                          label="Title"
                          :hide-details="nameErrors.length === 0"
                          :error-messages="nameErrors"
                        />
                      </v-col>

                      <v-col cols="12">
                        <div
                          class="text-caption grey--text text--darken-1 mb-2"
                        >
                          Description
                        </div>
                        <quill-editor
                          :content="Campaign.description"
                          :options="editorOption"
                          @change="onEditorChange($event)"
                        />
                      </v-col>

                      <v-col cols="12" class="mt-2">
                        <div class="d-flex align-center mb-2">
                          <div class="font-weight-bold">Banner Image</div>
                          <v-spacer />
                          <div class="text-caption grey--text text--darken-1">
                            Suggested: 1024 x 400
                          </div>
                        </div>

                        <v-card
                          class="upload-card"
                          outlined
                          hover
                          @click="pickFile"
                        >
                          <div v-if="Campaign.url" class="upload-preview">
                            <v-img :src="Campaign.url" height="180" />
                          </div>

                          <div v-else class="upload-placeholder">
                            <v-icon size="42" class="mb-2"
                              >mdi-image-plus</v-icon
                            >
                            <div class="font-weight-bold">Upload banner</div>
                            <div class="text-caption grey--text text--darken-1">
                              Click to choose an image
                            </div>
                          </div>
                        </v-card>

                        <input
                          type="file"
                          style="display: none"
                          ref="image"
                          accept="image/*"
                          @change="onFilePicked"
                        />
                      </v-col>

                      <v-col cols="12" class="mt-2">
                        <div class="font-weight-bold mb-2">Category</div>

                        <!-- Better UX than button list, still same behavior -->
                        <v-select
                          outlined
                          dense
                          :items="DonationCategories"
                          item-text="name"
                          item-value="id"
                          v-model="Campaign.DefaultDonationCategoryId"
                          label="Select Category"
                          :hide-details="DonationIdErrors.length === 0"
                          :error-messages="DonationIdErrors"
                          :loading="loadingCategories"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-tab-item>

                <!-- SETTINGS -->
                <v-tab-item>
                  <v-card-text class="pa-4">
                    <!-- Donation settings -->
                    <div v-if="Campaign.type === 'donation'">
                      <v-row dense>
                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">Goal Amount</div>
                          <div class="text-body-2 grey--text text--darken-1">
                            This does not stop donations, it only shows
                            progress.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            outlined
                            dense
                            type="number"
                            v-model="Campaign.dontationGoal"
                            label="Donation Goal"
                          />
                        </v-col>

                        <v-col cols="12">
                          <v-divider class="my-2" />
                        </v-col>

                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">Show Donate Count</div>
                          <div class="text-body-2 grey--text text--darken-1">
                            Show number of donations made.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4" class="text-right">
                          <v-switch
                            inset
                            v-model="Campaign.metadata.showDonateCount"
                          />
                        </v-col>

                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">
                            Show Visitors Count
                          </div>
                          <div class="text-body-2 grey--text text--darken-1">
                            Show number of page visitors.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4" class="text-right">
                          <v-switch
                            inset
                            v-model="Campaign.metadata.showVisitorsCount"
                          />
                        </v-col>

                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">Show Goal/Funds</div>
                          <div class="text-body-2 grey--text text--darken-1">
                            Show progress bar with raised vs goal.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4" class="text-right">
                          <v-switch
                            inset
                            v-model="Campaign.metadata.showGoalFunds"
                          />
                        </v-col>

                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">
                            Item Donation Limit
                          </div>
                          <div class="text-body-2 grey--text text--darken-1">
                            Lock a donation item when it reaches its target.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4" class="text-right">
                          <v-switch
                            inset
                            v-model="Campaign.metadata.donationLimit"
                          />
                        </v-col>

                        <!-- <v-col cols="12" md="6">
                          <v-select
                            dense
                            outlined
                            label="Sponsor Style"
                            :items="[
                              { text: 'Ramadan (Daily)', value: 'ramadan' },
                              {
                                text: 'Student Sponsorship (Qty)',
                                value: 'student',
                              },
                              { text: 'Normal (Simple)', value: 'normal' },
                              // later: { text: 'Utility Units (Qty)', value: 'units' }
                            ]"
                            v-model="Campaign.metadata.detailStyle"
                          />
                        </v-col> -->

                        <v-col cols="12">
                          <v-divider class="my-2" />
                        </v-col>

                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">
                            Pre Defined Donation Amounts
                          </div>
                          <div class="text-body-2 grey--text text--darken-1">
                            Add preset amounts like 10, 50, 100.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-btn color="success" block @click="addValue">
                            <v-icon left>mdi-plus</v-icon>
                            Add Amount
                          </v-btn>
                        </v-col>

                        <v-col cols="12">
                          <div class="d-flex flex-wrap">
                            <v-chip
                              v-for="(item, index) in preDefinedValue"
                              :key="index"
                              class="mr-2 mb-2"
                              label
                              close
                              @click:close="removeValue(index)"
                            >
                              {{ item | currency }}
                            </v-chip>

                            <div
                              v-if="preDefinedValue.length === 0"
                              class="text-caption grey--text text--darken-1"
                            >
                              No predefined amounts yet.
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </div>

                    <!-- Sponsor settings -->
                    <!-- Sponsor settings -->
                    <div v-else>
                      <v-row dense>
                        <!-- Sponsor Style -->
                        <v-col cols="12" md="6">
                          <v-select
                            dense
                            outlined
                            label="Sponsor Style"
                            :items="[
                              { text: 'Fast Collection', value: 'fast' },
                              { text: 'Student Sponsorship', value: 'student' },
                              { text: 'Unit Collection', value: 'units' },
                              { text: 'Normal (Simple)', value: 'normal' },
                            ]"
                            v-model="Campaign.metadata.detailStyle"
                          />
                        </v-col>

                        <v-col
                          cols="12"
                          md="6"
                          v-if="Campaign.metadata.detailStyle !== 'units'"
                        >
                          <v-select
                            dense
                            outlined
                            :items="durationTypes"
                            v-model="Campaign.metadata.durationType"
                            label="Duration Type"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </v-card-text>
                </v-tab-item>

                <v-tab-item v-if="Campaign.type === 'sponsor'">
                  <v-card-text class="pa-4">
                    <!-- Templates row moved here -->
                    <div class="d-flex align-center mb-3">
                      <div class="font-weight-bold text-subtitle-1">
                        Campaign Items
                      </div>

                      <v-spacer />

                      <v-btn
                        v-if="Campaign.metadata.detailStyle === 'fast'"
                        color="primary"
                        outlined
                        class="mr-2"
                        @click="applyTemplate('fast')"
                      >
                        <v-icon left>mdi-calendar-month</v-icon>
                        Add Fast Template
                      </v-btn>

                      <v-btn
                        v-if="Campaign.metadata.detailStyle === 'student'"
                        color="primary"
                        outlined
                        class="mr-2"
                        @click="applyTemplate('student')"
                      >
                        <v-icon left>mdi-account-school</v-icon>
                        Add Student Template
                      </v-btn>

                      <v-btn
                        v-if="Campaign.metadata.detailStyle === 'units'"
                        color="primary"
                        outlined
                        class="mr-2"
                        @click="applyTemplate('units')"
                      >
                        <v-icon left>mdi-cube</v-icon>
                        Add Unit Template
                      </v-btn>

                      <v-chip small color="grey lighten-3">
                        {{ (Campaign.CampaignDetails || []).length }} rows
                      </v-chip>
                    </div>

                    <CampaignDetails
                      :detailStyle="Campaign.metadata.detailStyle || 'normal'"
                      @submit="addDetail"
                    />

                    <v-divider class="my-3" />

                    <!-- Donation type can still use same details list if you want -->
                    <v-data-table
                      :headers="detailHeaders"
                      :items="Campaign.CampaignDetails"
                      hide-default-footer
                      :items-per-page="1000"
                      no-data-text="No items yet"
                      class="elevation-0"
                    >
                      <template v-slot:item="{ item, index }">
                        <tr>
                          <!-- delete -->
                          <td class="cell-actions">
                            <v-btn
                              icon
                              @click="deleteItem(index)"
                              v-if="!item.delete"
                            >
                              <v-icon color="red">mdi-delete</v-icon>
                            </v-btn>
                          </td>

                          <!-- dynamic editable cells -->
                          <td v-for="h in editableKeys" :key="h" class="py-1">
                            <v-text-field
                              v-model="item[h]"
                              outlined
                              dense
                              hide-details
                              :type="fieldType(h)"
                              @focus="$event.target.select()"
                            />
                          </td>
                        </tr>
                      </template>
                    </v-data-table>

                    <div class="text-caption grey--text text--darken-1 mt-2">
                      Tip: Add any extra keys (like visitors, date) and they
                      will appear automatically.
                    </div>
                  </v-card-text>
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- RIGHT: summary -->
        <v-col cols="12" md="4">
          <v-card outlined class="card-soft">
            <v-card-text class="pa-4">
              <div class="text-subtitle-1 font-weight-bold mb-2">Summary</div>

              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">Type</div>
                <div class="font-weight-bold">{{ Campaign.type || "-" }}</div>
              </div>

              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">Title</div>
                <div class="font-weight-bold text-truncate">
                  {{ Campaign.name || "-" }}
                </div>
              </div>

              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">
                  Category
                </div>
                <div class="font-weight-bold">
                  {{ selectedCategoryName }}
                </div>
              </div>

              <v-divider class="my-3" />

              <div class="d-flex justify-space-between">
                <div class="text-caption grey--text text--darken-1">
                  Publish
                </div>
                <div class="font-weight-bold">
                  {{ Campaign.isActive ? "Yes" : "No" }}
                </div>
              </div>

              <div class="d-flex justify-space-between mt-2">
                <div class="text-caption grey--text text--darken-1">Items</div>
                <div class="font-weight-bold">
                  {{ (Campaign.CampaignDetails || []).length }}
                </div>
              </div>

              <v-divider class="my-3" />

              <v-btn
                block
                color="primary"
                dark
                :loading="loading"
                :disabled="loading"
                @click="submit"
              >
                Save Campaign
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import campaignService from "../service.js";
import donationCategoryService from "@/modules/DonationCategory/service";
import CampaignDetails from "@/modules/Campaign/components/Details.vue";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import { quillEditor } from "vue-quill-editor";

export default {
  name: "create-campaign",
  mixins: [validationMixin],
  components: { CampaignDetails, quillEditor },

  validations: {
    Campaign: {
      name: { required },
      type: { required },
      DefaultDonationCategoryId: { required },
    },
  },

  data() {
    return {
      tab: 0,
      loading: false,
      loadingCategories: false,

      preDefinedValue: [],
      DonationCategories: [],
      durationTypes: ["monthly", "yearly"],

      Campaign: {
        name: null,
        description: null,
        type: "donation",
        DefaultDonationCategoryId: null,
        url: null,
        isActive: false,
        dontationGoal: null,
        intervalMonth: this.$moment().format("M"),
        intervalDays: this.$moment().format("D"),
        metadata: {
          preDefinedValue: [],
          donationLimit: false,
          durationType: "yearly",
          showGoalFunds: true,
          showVisitorsCount: true,
          showDonateCount: false,
          detailStyle: "normal",
        },
        CampaignDetails: [],
      },
      unitTemplate: [
        {
          category: "Utilities",
          name: "Electric Bill Units",
          amount: 20,
          totalQuantity: 0,
        },
        {
          category: "Utilities",
          name: "Gas Bill Units",
          amount: 20,
          totalQuantity: 0,
        },
      ],
      // your template stays the same (I kept your fields)
      ramadanTemplate: [
        {
          category: "First Ashra",
          name: "Day 1",
          amount: 550,
          date: "2026-02-19",
          day: "Thursday",
          visitors: 80,
        },
        {
          category: "First Ashra",
          name: "Day 2",
          amount: 800,
          date: "2026-02-20",
          day: "Friday",
          visitors: 100,
        },
        {
          category: "First Ashra",
          name: "Day 3",
          amount: 950,
          date: "2026-02-21",
          day: "Saturday",
          visitors: 135,
        },
        {
          category: "First Ashra",
          name: "Day 4",
          amount: 950,
          date: "2026-02-22",
          day: "Sunday",
          visitors: 130,
        },
        {
          category: "First Ashra",
          name: "Day 5",
          amount: 550,
          date: "2026-02-23",
          day: "Monday",
          visitors: 80,
        },
        {
          category: "First Ashra",
          name: "Day 6",
          amount: 550,
          date: "2026-02-24",
          day: "Tuesday",
          visitors: 80,
        },
        {
          category: "First Ashra",
          name: "Day 7",
          amount: 550,
          date: "2026-02-25",
          day: "Wednesday",
          visitors: 80,
        },
        {
          category: "First Ashra",
          name: "Day 8",
          amount: 550,
          date: "2026-02-26",
          day: "Thursday",
          visitors: 85,
        },
        {
          category: "First Ashra",
          name: "Day 9",
          amount: 850,
          date: "2026-02-27",
          day: "Friday",
          visitors: 100,
        },
        {
          category: "First Ashra",
          name: "Day 10",
          amount: 1050,
          date: "2026-02-28",
          day: "Saturday",
          visitors: 145,
        },

        {
          category: "Second Ashra",
          name: "Day 11",
          amount: 1000,
          date: "2026-03-01",
          day: "Sunday",
          visitors: 140,
        },
        {
          category: "Second Ashra",
          name: "Day 12",
          amount: 650,
          date: "2026-03-02",
          day: "Monday",
          visitors: 90,
        },
        {
          category: "Second Ashra",
          name: "Day 13",
          amount: 650,
          date: "2026-03-03",
          day: "Tuesday",
          visitors: 90,
        },
        {
          category: "Second Ashra",
          name: "Day 14",
          amount: 700,
          date: "2026-03-04",
          day: "Wednesday",
          visitors: 100,
        },
        {
          category: "Second Ashra",
          name: "Day 15",
          amount: 700,
          date: "2026-03-05",
          day: "Thursday",
          visitors: 100,
        },
        {
          category: "Second Ashra",
          name: "Day 16",
          amount: 1200,
          date: "2026-03-06",
          day: "Friday",
          visitors: 120,
        },
        {
          category: "Second Ashra",
          name: "Day 17",
          amount: 1300,
          date: "2026-03-07",
          day: "Saturday",
          visitors: 135,
        },
        {
          category: "Second Ashra",
          name: "Day 18",
          amount: 1200,
          date: "2026-03-08",
          day: "Sunday",
          visitors: 120,
        },
        {
          category: "Second Ashra",
          name: "Day 19",
          amount: 750,
          date: "2026-03-09",
          day: "Monday",
          visitors: 100,
        },
        {
          category: "Second Ashra",
          name: "Day 20",
          amount: 750,
          date: "2026-03-10",
          day: "Tuesday",
          visitors: 100,
        },

        {
          category: "Third Ashra",
          name: "Day 21",
          amount: 1100,
          date: "2026-03-11",
          day: "Wednesday",
          visitors: 150,
        },
        {
          category: "Third Ashra",
          name: "Day 22",
          amount: 1000,
          date: "2026-03-12",
          day: "Thursday",
          visitors: 140,
        },
        {
          category: "Third Ashra",
          name: "Day 23",
          amount: 1300,
          date: "2026-03-13",
          day: "Friday",
          visitors: 150,
        },
        {
          category: "Third Ashra",
          name: "Day 24",
          amount: 1400,
          date: "2026-03-14",
          day: "Saturday",
          visitors: 160,
        },
        {
          category: "Third Ashra",
          name: "Day 25",
          amount: 1300,
          date: "2026-03-15",
          day: "Sunday",
          visitors: 130,
        },
        {
          category: "Third Ashra",
          name: "Day 26",
          amount: 910,
          date: "2026-03-16",
          day: "Monday",
          visitors: 130,
        },
        {
          category: "Third Ashra",
          name: "Day 27",
          amount: 1350,
          date: "2026-03-17",
          day: "Tuesday",
          visitors: 180,
        },
        {
          category: "Third Ashra",
          name: "Day 28",
          amount: 1000,
          date: "2026-03-18",
          day: "Wednesday",
          visitors: 130,
        },
        {
          category: "Third Ashra",
          name: "Day 29",
          amount: 800,
          date: "2026-03-19",
          day: "Thursday",
          visitors: 130,
        },
        {
          category: "Third Ashra",
          name: "Day 30",
          amount: 750,
          date: "2026-03-20",
          day: "Friday",
          visitors: 90,
        },

        {
          category: "Suhoor",
          name: "Day 21",
          amount: 220,
          date: "2026-03-11",
          day: "Wednesday",
          visitors: 40,
        },
        {
          category: "Suhoor",
          name: "Day 22",
          amount: 220,
          date: "2026-03-12",
          day: "Thursday",
          visitors: 40,
        },
        {
          category: "Suhoor",
          name: "Day 23",
          amount: 220,
          date: "2026-03-13",
          day: "Friday",
          visitors: 40,
        },
        {
          category: "Suhoor",
          name: "Day 24",
          amount: 220,
          date: "2026-03-14",
          day: "Saturday",
          visitors: 40,
        },
        {
          category: "Suhoor",
          name: "Day 25",
          amount: 220,
          date: "2026-03-15",
          day: "Sunday",
          visitors: 40,
        },
        {
          category: "Suhoor",
          name: "Day 26",
          amount: 220,
          date: "2026-03-16",
          day: "Monday",
          visitors: 40,
        },
        {
          category: "Suhoor",
          name: "Day 27",
          amount: 350,
          date: "2026-03-17",
          day: "Tuesday",
          visitors: 70,
        },
        {
          category: "Suhoor",
          name: "Day 28",
          amount: 220,
          date: "2026-03-18",
          day: "Wednesday",
          visitors: 40,
        },
        {
          category: "Suhoor",
          name: "Day 29",
          amount: 220,
          date: "2026-03-19",
          day: "Thursday",
          visitors: 40,
        },
        {
          category: "Eid Day",
          name: "Eid Day 1",
          amount: 3500,
          date: "2026-03-19",
          day: "Thursday",
          visitors: 600,
        },
      ],
      editorOption: { placeholder: "Description" },
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser"]),

    selectedCategoryName() {
      const id = this.Campaign.DefaultDonationCategoryId;
      const found = (this.DonationCategories || []).find((x) => x.id === id);
      return found ? found.name : "-";
    },

    nameErrors() {
      const errors = [];
      if (!this.$v.Campaign.name.$dirty) return errors;
      if (!this.$v.Campaign.name.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      return errors;
    },

    DonationIdErrors() {
      const errors = [];
      if (!this.$v.Campaign.DefaultDonationCategoryId.$dirty) return errors;
      if (!this.$v.Campaign.DefaultDonationCategoryId.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      return errors;
    },

    // Collect all keys across items, so sponsor template fields show automatically
    editableKeys() {
      const style = this.Campaign?.metadata?.detailStyle || "normal";

      if (style === "fast") {
        return ["category", "name", "amount", "date", "day", "visitors"];
      }

      if (style === "student") {
        return ["category", "name", "amount", "totalQuantity"];
      }

      if (style === "units") {
        return ["category", "name", "amount", "totalQuantity"];
      }

      return ["category", "name", "amount"];
    },

    detailHeaders() {
      // first column is actions
      const headers = [
        { text: "", value: "__actions", sortable: false, align: "left" },
      ];
      for (const key of this.editableKeys) {
        headers.push({
          text: this.prettyHeader(key),
          value: key,
          sortable: false,
          align: key === "amount" || key === "visitors" ? "right" : "left",
        });
      }
      return headers;
    },
  },

  created() {
    this.getDonationCategories();
  },

  watch: {
    preDefinedValue: {
      deep: true,
      handler() {
        if (!this.Campaign.metadata) this.Campaign.metadata = {};
        this.Campaign.metadata.preDefinedValue = this.preDefinedValue;
      },
    },
    "Campaign.type"(t) {
      if (t === "sponsor") this.Campaign.dontationGoal = null;
      if (t === "donation") {
        if (!this.Campaign.metadata) this.Campaign.metadata = {};
        // choose a default style but it won't matter because Details tab hidden
        if (!this.Campaign.metadata.detailStyle)
          this.Campaign.metadata.detailStyle = "normal";
        // clear details because donation has none
        this.Campaign.CampaignDetails = [];
      }
      if (t === "donation" && this.tab === 2) this.tab = 0;
    },
    "Campaign.metadata.detailStyle"(val) {
      // optional, clears rows when style changes
      this.Campaign.CampaignDetails = [];

      if (!this.Campaign.metadata) this.Campaign.metadata = {};

      // ✅ units = forever monthly
      if (val === "units") {
        this.Campaign.metadata.durationType = "monthly";
        this.Campaign.metadata.allowForever = true;
        this.Campaign.metadata.endDate = null;
        this.Campaign.metadata.recurredCount = null;
      } else {
        // normal behavior
        this.Campaign.metadata.allowForever = false;
        if (!this.Campaign.metadata.durationType) {
          this.Campaign.metadata.durationType = "yearly";
        }
      }
    },
  },

  methods: {
    normalizeDetailsForApi() {
      const style = this.Campaign?.metadata?.detailStyle || "normal";

      const rows = Array.isArray(this.Campaign.CampaignDetails)
        ? this.Campaign.CampaignDetails
        : [];

      return rows.map((r) => {
        const row = { ...(r || {}) };

        // Normalize amount
        const amount = Number(row.amount) || 0;

        // Seats / capacity:
        // - For student/units templates you use totalQuantity
        // - For normal/fast you might not have it, keep as null (unlimited)
        let capacity = null;

        if (
          row.totalQuantity !== undefined &&
          row.totalQuantity !== null &&
          row.totalQuantity !== ""
        ) {
          const n = Number(row.totalQuantity);
          capacity = Number.isFinite(n) ? n : null;
        }

        // soldQty should be 0 at create time unless you explicitly set it
        let soldQty = 0;
        if (
          row.soldQuantity !== undefined &&
          row.soldQuantity !== null &&
          row.soldQuantity !== ""
        ) {
          const n = Number(row.soldQuantity);
          soldQty = Number.isFinite(n) ? n : 0;
        }

        // Store everything else in metadata (so you can reuse for units later)
        const metadata = {
          ...(row.metadata || {}),
          detailStyle: style,

          // keep your “template fields”
          date: row.date,
          day: row.day,
          visitors: row.visitors,

          pricingMode: row.pricingMode,
          capMode: row.capMode,
          allowQuantity: row.allowQuantity,
        };

        // Cleanup undefined keys so metadata stays tidy
        Object.keys(metadata).forEach(
          (k) => metadata[k] === undefined && delete metadata[k]
        );

        return {
          // real columns
          category: row.category,
          name: row.name,
          amount,

          // ✅ new columns from migration
          capacity, // <-- seats available
          soldQty, // <-- already sold

          metadata,
        };
      });
    },
    applyTemplate(style) {
      // Fast Collection (Ramzan)
      if (style === "fast") {
        this.Campaign.CampaignDetails = JSON.parse(
          JSON.stringify(this.ramadanTemplate)
        );
        this.tab = 2;
        return;
      }

      // Student Sponsorship
      if (style === "student") {
        this.Campaign.CampaignDetails.push({
          category: "Students",
          name: "Student Sponsorship",
          amount: 0,
          pricingMode: "unit",
          capMode: "quantity",
          allowQuantity: true,
          totalQuantity: 0,
          soldQuantity: 0,
        });
        this.tab = 2;
        return;
      }

      // Unit Collection
      if (style === "units") {
        // Option A: add 1 default row
        this.Campaign.CampaignDetails.push({
          category: "Utilities",
          name: "Utility Units",
          amount: 20,
          pricingMode: "unit",
          capMode: "quantity",
          allowQuantity: true,
          totalQuantity: 0, // available units
          soldQuantity: 0,
        });

        // Option B: instead add multiple predefined rows
        // this.Campaign.CampaignDetails = JSON.parse(JSON.stringify(this.unitTemplate));

        this.tab = 2;
        return;
      }
    },
    prettyHeader(key) {
      // simple nicer labels
      const map = {
        dontationGoal: "Donation Goal",
        donationAmount: "Donation Amount",
        DefaultDonationCategoryId: "Category",
      };
      if (map[key]) return map[key];

      // title case from camel/snake
      return String(key)
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },

    fieldType(key) {
      const numeric = [
        "amount",
        "visitors",
        "qty",
        "received",
        "donationAmount",
        "dontationGoal",
      ];
      if (["totalQuantity", "soldQuantity", "installmentsCount"].includes(key))
        return "number";
      if (["allowQuantity", "allowForever"].includes(key)) return "checkbox"; // see note below
      if (["pricingMode", "capMode", "billingMode"].includes(key))
        return "select";
      if (numeric.includes(key)) return "number";
      if (key === "date") return "date"; // HTML date input
      return "text";
    },

    onEditorChange({ html }) {
      this.Campaign.description = html;
    },

    addTemplate() {
      // keeps any current edits? If you prefer merge, say so
      this.Campaign.CampaignDetails = JSON.parse(
        JSON.stringify(this.ramadanTemplate)
      );
      this.tab = 2; // jump to Details
    },

    removeValue(index) {
      this.preDefinedValue.splice(index, 1);
    },

    addValue() {
      this.$swal
        .fire({
          title: "Enter Value",
          input: "number",
          showCancelButton: true,
          confirmButtonText: "Save",
          allowOutsideClick: () => !this.$swal.isLoading(),
        })
        .then((result) => {
          if (result.value) this.preDefinedValue.push(result.value);
          else {
            this.$swal.fire({ title: "Please enter valid value." });
          }
        });
    },

    deleteItem(i) {
      this.Campaign.CampaignDetails.splice(i, 1);
    },

    addDetail(val) {
      this.Campaign.CampaignDetails.push(val);
      this.tab = 2;
    },

    pickFile() {
      this.$refs.image.click();
    },

    onFilePicked(e) {
      const { files } = e.target;
      if (!files || !files[0]) return;

      const fr = new FileReader();
      fr.readAsDataURL(files[0]);
      fr.addEventListener("load", () => {
        this.Campaign.url = fr.result;
      });
    },

    getDonationCategories() {
      this.loadingCategories = true;
      return donationCategoryService
        .getAll({ publicDonation: true })
        .then((result) => {
          this.DonationCategories = result.data || [];
          this.loadingCategories = false;
        })
        .catch(() => {
          this.loadingCategories = false;
        });
    },

    close() {
      this.empty();
      this.$router.go(-1);
    },

    empty() {
      this.loading = false;
      this.tab = 0;
      this.preDefinedValue = [];
      this.Campaign = {
        name: null,
        description: null,
        type: "donation",
        DefaultDonationCategoryId: null,
        isActive: false,
        dontationGoal: null,
        url: null,
        metadata: {
          donationLimit: false,
          durationType: "yearly",
          showGoalFunds: true,
          showVisitorsCount: true,
          showDonateCount: false,
          preDefinedValue: [],
        },
        CampaignDetails: [],
      };
      this.$v.$reset();
    },

    submit() {
      // keep your existing behavior
      if (!this.Campaign.metadata) this.Campaign.metadata = {};
      this.Campaign.metadata.preDefinedValue = this.preDefinedValue;

      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
        return;
      }

      if (this.Campaign.type === "sponsor") {
        const rows = Array.isArray(this.Campaign.CampaignDetails)
          ? this.Campaign.CampaignDetails
          : [];
        if (rows.length === 0) {
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: "Please add at least 1 campaign item.",
            });
          return;
        }
      }

      this.loading = true;

      this.Campaign.CampaignDetails = this.normalizeDetailsForApi();
      return campaignService
        .create(this.Campaign)
        .then((result) => {
          if (result.status) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({ icon: "success", title: "Campaign is created" });

            this.loading = false;
            this.$emit("submit");
            this.close();
          } else {
            this.loading = false;
          }
        })
        .catch((err) => {
          this.loading = false;
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: err?.data?.message || "Failed to create campaign",
            });
        });
    },
  },
};
</script>

<style scoped>
.touch-view-container {
  height: calc(100vh - 48px);
  overflow: auto;
  padding-top: 12px;
}

.card-soft {
  border-radius: 16px;
  overflow: hidden;
}

.upload-card {
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
}

.upload-placeholder {
  height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-preview {
  border-radius: 14px;
  overflow: hidden;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-actions {
  width: 54px;
}
</style>